import { Component, computed, input, output, signal } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { OverlayModule, ConnectedPosition } from '@angular/cdk/overlay';
import type { Company, CompanyMembership } from '@typings/app/company.types';
import type { EmployerRole } from '@typings/app/rbac.types';

/**
 * Sidebar workspace block: shows the active company (logo/initial + name) at the
 * top of the rail and, when the employer belongs to more than one company, opens
 * a menu to switch between them. With a single membership it renders as a static,
 * non-interactive block.
 *
 * Presentational only — the parent owns the session call and navigation, reacting
 * to `switchCompany`. The switch menu uses a body-level CDK overlay so the rail's
 * `overflow: hidden` never clips it.
 */
@Component({
  selector: 'app-sidebar-company',
  standalone: true,
  imports: [NgTemplateOutlet, MatIconModule, TranslateModule, OverlayModule],
  templateUrl: './sidebar-company.component.html',
  // The switch-menu panel styles live in `sidebar-company.component.global.scss`,
  // registered globally from `styles.scss` (NOT here): the panel renders in a
  // body-level CDK overlay, so view-encapsulated styles can't reach it.
  styleUrl: './sidebar-company.component.scss',
})
export class SidebarCompanyComponent {
  /** The currently active company shown in the trigger. */
  company = input.required<Company | null>();
  /** Every company the employer belongs to (drives the switch menu). */
  memberships = input.required<CompanyMembership[]>();
  /** When true the rail is collapsed — only the avatar shows. */
  collapsed = input(false);

  /** Emitted with the chosen company id when the user picks a different company. */
  switchCompany = output<string>();

  readonly menuOpen = signal(false);
  readonly canSwitch = computed(() => this.memberships().length > 1);
  readonly initial = computed(() => this.initialOf(this.company()?.name));

  /** Below-start with an above-start fallback; 'start' is direction-aware (RTL-safe). */
  readonly menuPositions: ConnectedPosition[] = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 6 },
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -6 },
  ];

  roleKey(role: EmployerRole): string {
    return `auth.select_company.roles.${role}`;
  }

  initialOf(name?: string): string {
    return (name ?? '').trim().charAt(0).toUpperCase() || '?';
  }

  toggleMenu(): void {
    if (this.canSwitch()) this.menuOpen.update((open) => !open);
  }

  closeMenu(): void {
    this.menuOpen.set(false);
  }

  onSelect(companyId: string): void {
    this.closeMenu();
    if (companyId !== this.company()?.id) this.switchCompany.emit(companyId);
  }
}
